/**
 * PNG export for a rendered React Flow graph.
 *
 * Strategy: temporarily fit the graph into view, snapshot the React Flow
 * viewport DOM via html-to-image at 2× device pixel ratio, then trigger a
 * download. Waits for `document.fonts.ready` first so webfonts render in
 * the export (the same gotcha the previous Cytoscape path had).
 */

import { toPng } from "html-to-image";

export async function exportGraphPng(
  container: HTMLElement,
  sessionName: string,
): Promise<void> {
  // Wait for webfonts so text renders correctly in the export
  await document.fonts.ready;

  // React Flow renders the actual graph into `.react-flow__viewport`. The
  // outer container also includes the controls + minimap; we want just the
  // graph itself.
  const viewport = container.querySelector<HTMLElement>(
    ".react-flow__viewport",
  );
  const target = viewport ?? container;

  // Compute the bounding box of the laid-out nodes so the export captures
  // the whole graph, not just what's currently visible in the pane.
  const nodes = container.querySelectorAll<HTMLElement>(".react-flow__node");
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  nodes.forEach((n) => {
    const r = n.getBoundingClientRect();
    minX = Math.min(minX, r.left);
    minY = Math.min(minY, r.top);
    maxX = Math.max(maxX, r.right);
    maxY = Math.max(maxY, r.bottom);
  });

  // html-to-image accepts width/height options and infers the canvas size.
  // We pad the bounds a little so labels don't get cut at the edge.
  const padding = 40;
  const hasBounds = isFinite(minX) && isFinite(maxX);
  const width = hasBounds ? Math.ceil(maxX - minX) + padding * 2 : undefined;
  const height = hasBounds ? Math.ceil(maxY - minY) + padding * 2 : undefined;

  const dataUrl = await toPng(target, {
    backgroundColor: "#ffffff",
    pixelRatio: 2,
    cacheBust: true,
    width,
    height,
    style: hasBounds
      ? {
          transform: `translate(${-minX + padding}px, ${-minY + padding}px)`,
          transformOrigin: "top left",
        }
      : undefined,
  });

  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = `${sanitizeFilename(sessionName)}.png`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function sanitizeFilename(name: string): string {
  return (
    name
      .trim()
      .replace(/[^a-z0-9_\-. ]/gi, "_")
      .replace(/\s+/g, "_")
      .slice(0, 100) || "tabgraph-export"
  );
}
